/**
 * features/reports/DashboardPage.tsx
 */

import { useState } from 'react';
import { FileCheck2, Receipt, TrendingDown, TrendingUp } from 'lucide-react';
import { formatMoney, formatMonthYearThai } from '@ft/core';
import { PageHeader, StatCard } from '~/ui/layout';
import { Card, CardHeader, Select, Skeleton } from '~/ui/primitives';
import { useDashboard, useMonthlySeries } from './hooks';
import { MonthlyBarChart } from './MonthlyBarChart';

const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const CURRENT_MONTH = now.getMonth() + 1;

const YEAR_OPTIONS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => index + 1);

export function DashboardPage() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(CURRENT_MONTH);

  const { data: dashboard, isLoading } = useDashboard(year, month);
  const { data: series, isLoading: seriesLoading } = useMonthlySeries(year);

  return (
    <div>
      <PageHeader
        title="แดชบอร์ด"
        description={formatMonthYearThai(year, month)}
        actions={
          <div className="flex gap-2">
            <Select value={month} onChange={(event) => setMonth(Number(event.target.value))} className="w-32">
              {MONTH_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {formatMonthYearThai(year, value).split(' ')[0]}
                </option>
              ))}
            </Select>
            <Select value={year} onChange={(event) => setYear(Number(event.target.value))} className="w-24">
              {YEAR_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value + 543}
                </option>
              ))}
            </Select>
          </div>
        }
      />

      {isLoading || !dashboard ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="รายรับ"
            value={`${formatMoney(dashboard.revenue)} บาท`}
            sublabel={`${dashboard.invoiceCount.toLocaleString('th-TH')} ใบเสร็จ/ใบกำกับภาษี`}
            tone="success"
            icon={<TrendingUp size={16} />}
          />
          <StatCard
            label="ค่าใช้จ่าย"
            value={`${formatMoney(dashboard.expense)} บาท`}
            sublabel={`${dashboard.expenseCount.toLocaleString('th-TH')} รายการ`}
            tone="danger"
            icon={<TrendingDown size={16} />}
          />
          <StatCard
            label="กำไร"
            value={`${formatMoney(dashboard.profit)} บาท`}
            tone={dashboard.profit >= 0 ? 'success' : 'danger'}
            icon={<Receipt size={16} />}
          />
          <StatCard
            label="ใบเสนอราคา"
            value={`${dashboard.totalQuotations.toLocaleString('th-TH')} ใบ`}
            sublabel={`อนุมัติแล้ว ${dashboard.acceptedQuotations.toLocaleString('th-TH')} ใบ`}
            icon={<FileCheck2 size={16} />}
          />
        </div>
      )}

      <div className="mt-5">
        <Card>
          <CardHeader title="รายรับ-รายจ่ายรายเดือน" description={`ปี ${year + 543}`} />
          <div className="card-body">
            {seriesLoading || !series ? (
              <Skeleton className="h-44" />
            ) : (
              <MonthlyBarChart data={series} />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
